import { Formik } from "formik";
import { useFormSubmit, Drawer as DrawerType } from "melco-shared-logic";
import { useEffect, useState } from "react";
import { object, string } from "yup";
import { useDataAssignments } from "../../hooks/inputFormats/useDataAssignments";
import { useFilenameRules } from "../../hooks/inputFormats/useFilenameRules";
import { DisplayFilenameRuleForm } from "./DisplayFilenameRuleForm";
import { FilenameRuleFormMode } from "./FilenameRuleDrawer";
import { ADVANCED_FILENAME_RULE_TYPES } from "./SelectFilenameRuleType";

type FilenameRuleFormValueType = {
  type?: string;
  value?: string;
  format_detail_id?: string;
  exception_id?: string;
};

type FilenameRuleFormProps = {
  drawer: DrawerType;
  editFilenameRuleIndex: number | undefined;
  formMode: FilenameRuleFormMode;
};

const FilenameRuleSchema = object().shape({
  type: string().oneOf(ADVANCED_FILENAME_RULE_TYPES),
  value: string().when("type", {
    is: "Constant",
    then: (schema) => schema.required("global.error.required"),
  }),
  format_detail_id: string().when("type", {
    is: (val: string) => val !== "Constant",
    then: (schema) => schema.required("global.error.required"),
  }),
});

const newFilenameRuleInitialValues: FilenameRuleFormValueType = {
  type: "Dynamic",
  value: "",
  format_detail_id: "",
  exception_id: "",
};

export const FilenameRuleForm: React.FC<FilenameRuleFormProps> = ({
  drawer,
  editFilenameRuleIndex,
  formMode,
}) => {
  const dataAssignments = useDataAssignments();

  const isAddingNewRule = editFilenameRuleIndex == null;

  const { closeDrawer } = drawer;

  const [initialValues, setInitialValues] = useState(
    newFilenameRuleInitialValues
  );

  const { filenameRules, addFilenameRule, updateFilenameRuleAtIndex } =
    useFilenameRules();

  useEffect(() => {
    if (isAddingNewRule) {
      setInitialValues(newFilenameRuleInitialValues);
      return;
    }

    setInitialValues(
      (filenameRules[editFilenameRuleIndex] as FilenameRuleFormValueType) ??
        newFilenameRuleInitialValues
    );
  }, [filenameRules, editFilenameRuleIndex, setInitialValues]);

  const onSubmit = useFormSubmit<FilenameRuleFormValueType>(
    async (values: FilenameRuleFormValueType) => {
      if (isAddingNewRule) {
        addFilenameRule(values);
      } else {
        updateFilenameRuleAtIndex(editFilenameRuleIndex, values);
      }

      return { successMessages: [{ message: "default" }] };
    },
    {
      translationPrefix: `inputFormats.actions.filenameRules.actions.${formMode}`,

      onSuccess: (_, { resetForm }) => {
        closeDrawer();
        resetForm({ values: initialValues });
      },
    }
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={FilenameRuleSchema}
    >
      <DisplayFilenameRuleForm
        dataAssignments={dataAssignments}
        filenameRules={filenameRules}
        formMode={formMode}
        isAddingNewRule={isAddingNewRule}
      />
    </Formik>
  );
};
